// Publish/subscribe policy (F-CALL-1): simulcast on, adaptive streaming on
// (off-screen tiles drop to low layer / pause — pairs with §9.3.6), dynacast
// so unsubscribed layers are not encoded.

import { AudioPresets, RoomOptions, VideoPresets } from 'livekit-client';

/** Org audio policy (from the admission grant). */
export interface AudioConfig {
  stereo: boolean;
  echoCancellation: boolean;
  noiseSuppression: boolean;
  autoGainControl: boolean;
}

// Today's safe default: mono with the WebRTC processing on.
const DEFAULT_AUDIO: AudioConfig = {
  stereo: false,
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

export function defaultRoomOptions(audio: AudioConfig = DEFAULT_AUDIO): RoomOptions {
  // Stereo hi-fi is a distinct MODE, not an add-on: the browser's audio
  // processing pipeline is mono, so any of echo/noise/gain being on forces a
  // mono downmix. Stereo therefore overrides all three to OFF; they apply only
  // in mono mode.
  const stereo = audio.stereo;
  return {
    adaptiveStream: true,
    dynacast: true,
    publishDefaults: {
      simulcast: true,
      videoSimulcastLayers: [VideoPresets.h180, VideoPresets.h360],
      // Screen share is published NON-simulcast, as a single adaptive layer —
      // configured per-publish in setScreenShareEnabled (room.ts). A two-layer
      // screen share had a bandwidth dead zone that collapsed it below HD.
      audioPreset: stereo ? AudioPresets.musicHighQualityStereo : AudioPresets.music,
      dtx: !stereo, // keep audio continuous for hi-fi; DTX is a speech optimization
    },
    videoCaptureDefaults: {
      resolution: VideoPresets.h720.resolution,
    },
    audioCaptureDefaults: {
      channelCount: stereo ? 2 : 1,
      echoCancellation: stereo ? false : audio.echoCancellation,
      noiseSuppression: stereo ? false : audio.noiseSuppression,
      autoGainControl: stereo ? false : audio.autoGainControl,
    },
  };
}
